import { useEffect, useMemo, useState } from "react";

import { Skeleton } from "@/components/ui/Skeleton";
import type { TypeDetail } from "@/customer/catalog/types";

import { PriceBreakdown } from "./PriceBreakdown";
import { useQuote } from "./api";
import type { QuoteInput } from "./types";

// Date-range picker + add-on toggles that drive a live PriceBreakdown.
// Pure preview; the Reserve button owns the transactional hand-off to
// checkout. Native <input type="date"> for iOS/Android calendar UX;
// debounced 250ms so keystroke-driven changes don't hammer the server.

interface Props {
  type: TypeDetail;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function todayPlusNoticeDays(noticeHours: number): string {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const noticeDays = Math.ceil(noticeHours / 24);
  now.setDate(now.getDate() + noticeDays);
  return now.toISOString().slice(0, 10);
}

function isoPlusDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

// Phase 4c rate-card overhaul (FR-3, FR-5) — quotes are always whole-week
// multiples; the 7-day floor is enforced by the server.
const MIN_RENTAL_DAYS = 7;

export function QuoteEstimator({ type }: Props): JSX.Element {
  // Seed with the earliest legal start + a 1-week window so the
  // breakdown renders something useful on first paint.
  const defaultStart = useMemo(() => todayPlusNoticeDays(type.notice_required_hours), [type.notice_required_hours]);
  // 7-day floor: pickup + 6 days = 7 days inclusive.
  const defaultEnd = useMemo(() => isoPlusDays(defaultStart, MIN_RENTAL_DAYS - 1), [defaultStart]);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState<number[]>([]);
  const [damageWaiver, setDamageWaiver] = useState(false);
  const [delivery, setDelivery] = useState(true);

  // The type may not allow pickup at all — force delivery=true in that case.
  useEffect(() => {
    if (type.delivery_only && !delivery) setDelivery(true);
  }, [type.delivery_only, delivery]);

  // Debounce the input before triggering the query.
  const debounced = useDebounced({
    startDate,
    endDate,
    selectedAttachmentIds,
    damageWaiver,
    delivery
  }, 250);

  const datesAreValid =
    ISO_DATE.test(debounced.startDate) && ISO_DATE.test(debounced.endDate);

  const quoteInput: QuoteInput | null = datesAreValid
    ? {
        type_slug: type.slug,
        start_date: debounced.startDate,
        end_date: debounced.endDate,
        // Sort so that toggling A then B produces the same react-query
        // cache key as toggling B then A — otherwise we double-fetch.
        attachment_type_ids: [...debounced.selectedAttachmentIds].sort((a, b) => a - b),
        operator_hours: 0,
        damage_waiver: debounced.damageWaiver,
        delivery: debounced.delivery
      }
    : null;

  const { data, isPending, isFetching } = useQuote(quoteInput);

  return (
    <section
      aria-labelledby="estimate-heading"
      className="rounded-md border border-zinc-200 bg-brand-white p-4 space-y-4"
    >
      <h2 id="estimate-heading" className="text-base font-semibold text-brand-black">
        Quick estimate
      </h2>

      <div className="grid gap-3 sm:grid-cols-2">
        <DateField
          id="estimate-start"
          label="Pickup / delivery date"
          value={startDate}
          min={defaultStart}
          onChange={setStartDate}
        />
        <DateField
          id="estimate-end"
          label="Return date"
          value={endDate}
          min={startDate}
          onChange={setEndDate}
        />
      </div>

      {type.attachments.length > 0 ? (
        <fieldset className="space-y-1">
          <legend className="text-sm font-medium text-brand-black">Add-on attachments</legend>
          <ul className="space-y-1">
            {type.attachments.map((att) => (
              <li key={att.id}>
                <label className="flex items-center gap-2 text-sm text-brand-black">
                  <input
                    type="checkbox"
                    checked={selectedAttachmentIds.includes(att.id)}
                    onChange={(e) => {
                      setSelectedAttachmentIds((prev) =>
                        e.target.checked ? [...prev, att.id] : prev.filter((id) => id !== att.id)
                      );
                    }}
                  />
                  <span>{att.name}</span>
                </label>
              </li>
            ))}
          </ul>
        </fieldset>
      ) : null}

      {type.damage_waiver_percent > 0 ? (
        <label className="flex items-center gap-2 text-sm text-brand-black">
          <input
            type="checkbox"
            checked={damageWaiver}
            onChange={(e) => setDamageWaiver(e.target.checked)}
          />
          <span>
            Add damage waiver ({Math.round(type.damage_waiver_percent * 100)}% of rental)
          </span>
        </label>
      ) : null}

      {type.pickup_allowed && !type.delivery_only ? (
        <label className="flex items-center gap-2 text-sm text-brand-black">
          <input
            type="checkbox"
            checked={!delivery}
            onChange={(e) => setDelivery(!e.target.checked)}
          />
          <span>I&apos;ll pick this up myself (skip delivery fee)</span>
        </label>
      ) : null}

      <div aria-busy={isFetching}>
        {!datesAreValid ? (
          <p className="text-sm text-zinc-500">Pick both dates to see your estimate.</p>
        ) : isPending && !data ? (
          <Skeleton className="h-32 w-full" />
        ) : null}
        {/* Live region wraps only the meaningful result so screen readers
            announce the price update rather than the transient skeleton. */}
        <div aria-live="polite">
          {datesAreValid && data ? <PriceBreakdown quote={data} /> : null}
        </div>
      </div>
    </section>
  );
}

function DateField({
  id,
  label,
  value,
  min,
  onChange
}: {
  id: string;
  label: string;
  value: string;
  min: string;
  onChange: (next: string) => void;
}): JSX.Element {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-brand-black">
        {label}
      </label>
      <input
        id={id}
        type="date"
        value={value}
        min={min}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-brand-red focus:outline-none focus:ring-1 focus:ring-brand-red"
      />
    </div>
  );
}

// Lightweight debounce so each keystroke in the date input doesn't fire
// a quote. We snapshot the whole input object together so all fields
// settle into the same downstream query.
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}
